import { useState, useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Login from "@/pages/Login";
import Layout from "@/components/Layout";
import "@/App.css";

// Route-level code-splitting — each page becomes its own chunk so the
// initial JS payload stays small (Dashboard alone was ~90 KB gzipped).
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const CreateQuotation = lazy(() => import("@/pages/CreateQuotation"));
const EditQuotation = lazy(() => import("@/pages/EditQuotation"));
const DuplicateQuotation = lazy(() => import("@/pages/DuplicateQuotation"));
const RepeatItems = lazy(() => import("@/pages/RepeatItems"));
const CustomerDetail = lazy(() => import("@/pages/CustomerDetail"));
const CustomerDatabase = lazy(() => import("@/pages/CustomerDatabase"));
const AdminPanel = lazy(() => import("@/pages/AdminPanel"));
const AdminHistory = lazy(() => import("@/pages/AdminHistory"));
const CatalogueManager = lazy(() => import("@/pages/CatalogueManager"));
const SmartQuote = lazy(() => import("@/pages/SmartQuote"));
const PublicQuotation = lazy(() => import("@/pages/PublicQuotation"));
const PublicCatalogue = lazy(() => import("@/pages/PublicCatalogue"));
const SelfQuote = lazy(() => import("@/pages/SelfQuote"));

const RouteFallback = () => (
  <div className="min-h-[40vh] flex items-center justify-center text-[#94a3b8] text-sm">
    <div className="inline-flex items-center gap-2">
      <span className="w-4 h-4 rounded-full border-2 border-[#dc2626] border-t-transparent animate-spin" />
      Loading…
    </div>
  </div>
);

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const userData = localStorage.getItem("user");
    
    if (token && userData) {
      setIsAuthenticated(true);
      setUser(JSON.parse(userData));
    }
    setLoading(false);
  }, []);

  const handleLogin = (token, userData) => {
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(userData));
    setIsAuthenticated(true);
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setIsAuthenticated(false);
    setUser(null);
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
          {/* Public client route - no auth, no Layout */}
          <Route path="/q/:token" element={<PublicQuotation />} />
          {/* Public catalogue - fully public, no auth, no Layout */}
          <Route path="/catalogue" element={<PublicCatalogue />} />
          {/* Customer self-quote builder - fully public, no auth, no Layout */}
          <Route path="/self-quote" element={<SelfQuote />} />

          <Route path="/login" element={
            isAuthenticated ? <Navigate to="/" replace /> : <Login onLogin={handleLogin} />
          } />
          
          <Route path="/" element={
            isAuthenticated ? <Layout user={user} onLogout={handleLogout} /> : <Navigate to="/login" replace />
          }>
            <Route index element={<Dashboard />} />
            <Route path="create" element={<CreateQuotation />} />
            <Route path="edit/:id" element={<EditQuotation />} />
            <Route path="duplicate/:id" element={<DuplicateQuotation />} />
            <Route path="repeat-items/:id" element={<RepeatItems />} />
            <Route path="customer/:contact" element={<CustomerDetail />} />
            <Route path="customers" element={<CustomerDatabase />} />
            <Route path="admin" element={
              user?.is_admin ? <AdminPanel /> : <Navigate to="/" replace />
            } />
            <Route path="admin/history" element={
              user?.is_admin ? <AdminHistory /> : <Navigate to="/" replace />
            } />
            <Route path="admin/catalogue" element={<CatalogueManager />} />
            <Route path="smart-quote" element={<SmartQuote />} />
          </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </div>
  );
}

export default App;